/**
 * StudioRings — Apple Watch-style activity rings for the Today page.
 *
 * Three concentric rings, each tracking one weekly studio momentum
 * signal:
 *
 *   • Outer  · BOOKINGS · shoots scheduled / shot this week
 *   • Middle · PIPELINE · inquiries replied to / actioned this week
 *   • Inner  · REVENUE  · payments received this week
 *
 * Studios feel a week shape better than a list of dates. One look at
 * the rings tells them whether they're "in motion" or coasting. The
 * goal-relative fill is what makes it personal — beat last week, hit
 * 100%, see the ring close.
 *
 * Each ring is tappable and deep-links to the relevant surface
 * (calendar, inquiries, finances). Tap-target ergonomics matter on
 * touch laptops + iPad mobile-web — every ring is its own button.
 *
 * Animation: rings fill from 0 to current on mount via SVG
 * strokeDashoffset interpolation. Numbers count up in parallel via
 * the existing CountingNumber. Both eased on easeOutCubic. Reuses
 * the visual language of Apple's Fitness rings — saturated, solid,
 * trailing-edge cap for the soft-stop look.
 */
import { useMemo, useState, useEffect } from 'react';
import { Camera, Inbox, DollarSign, ArrowRight } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { useProjects } from '@/services/projects';
import { useInquiries } from '@/services/inquiries';
import { useStripePayments } from '@/services/stripeConnect';
import { MetricDetailSheet } from './MetricDetailSheet';

interface RingSpec {
  /** Stable id used as key + analytics tag. */
  id: 'bookings' | 'pipeline' | 'revenue';
  /** Short label that sits next to the ring. ALL CAPS. */
  label: string;
  /** Achieved value for this week. */
  value: number;
  /** Target the ring is filling toward. */
  goal: number;
  /** Display formatter for the value (e.g., "$" prefix). */
  format: (v: number) => string;
  /** Ring stroke color — Apple-Fitness-saturated. */
  color: string;
  /** Track color (the unfilled background ring). */
  track: string;
  /** Glow color used for the active ring's soft halo. */
  glow: string;
  /** Where the ring deep-links to on tap. */
  href: string;
  /** Icon next to the number when the ring's expanded label renders. */
  icon: React.ComponentType<{ className?: string }>;
}

export default function StudioRings() {
  // ── Data ─────────────────────────────────────────────────────────
  // All three signals share a single "this week" window — Monday
  // 00:00 local through Sunday 23:59 local. Recomputed once per
  // mount; refresh is implicit via the underlying repository hooks.
  const weekRange = useMemo(() => weekWindow(), []);

  const { data: projects = [] } = useProjects();
  const { data: inquiries = [] } = useInquiries();
  const { data: payments = [] } = useStripePayments();

  const bookings = useMemo(() => {
    // Projects come back with mixed casing depending on the source
    // (Supabase rows = snake_case; some adapters camelCase). Look up
    // both so the count works across real + demo data.
    return projects.filter((p: any) => {
      const ref = p.scheduled_date ?? p.scheduledDate;
      const d = ref ? new Date(ref) : null;
      return d && d >= weekRange.start && d <= weekRange.end;
    }).length;
  }, [projects, weekRange]);

  const pipeline = useMemo(() => {
    // "Actioned this week" = touched within the week, regardless of
    // final stage. Catches "i replied", "i moved them to booked",
    // "i archived" — all signs of pipeline hygiene. Falls back to
    // submitted-at if no updated-at exists (older inquiry rows).
    return inquiries.filter((i: any) => {
      const ref =
        i.updatedAt ?? i.updated_at ?? i.submittedAt ?? i.submitted_at;
      const d = ref ? new Date(ref) : null;
      return d && d >= weekRange.start && d <= weekRange.end;
    }).length;
  }, [inquiries, weekRange]);

  const revenue = useMemo(() => {
    return payments
      .filter((p: any) => {
        const ref = p.created_at ?? p.createdAt;
        const d = ref ? new Date(ref) : null;
        return d && d >= weekRange.start && d <= weekRange.end;
      })
      .reduce(
        (sum: number, p: any) =>
          sum + ((p.amount_cents ?? p.amountCents ?? 0) as number) / 100,
        0,
      );
  }, [payments, weekRange]);

  // ── Ring config ─────────────────────────────────────────────────
  // Goals are sensible defaults for v1. v2: read from the goals
  // table so studios can tune each ring's target.
  const rings: RingSpec[] = [
    {
      id: 'bookings',
      label: 'Bookings',
      value: bookings,
      goal: 3,
      format: (v) => String(Math.round(v)),
      color: 'hsl(var(--orbit-orange))',
      track: 'rgba(212, 84, 31, 0.12)',
      glow: 'rgba(212, 84, 31, 0.30)',
      href: '/calendar',
      icon: Camera,
    },
    {
      id: 'pipeline',
      label: 'Pipeline',
      value: pipeline,
      goal: 5,
      format: (v) => String(Math.round(v)),
      color: 'hsl(var(--lunar-teal))',
      track: 'rgba(45, 110, 112, 0.12)',
      glow: 'rgba(45, 110, 112, 0.28)',
      href: '/inquiries',
      icon: Inbox,
    },
    {
      id: 'revenue',
      label: 'Revenue',
      // POL-10 — was `#E8C547` (bright mustard yellow), which was
      // the loudest yellow stroke on the Today page and read as
      // "highlighter" against the sky.  Swapped to `#D4B896`
      // (champagne) so the ring still carries a warm-metallic
      // "money" cue but sits inside the editorial palette instead
      // of shouting.
      value: revenue,
      goal: 2500,
      format: (v) => `$${Math.round(v).toLocaleString()}`,
      color: '#D4B896',
      track: 'rgba(212, 184, 150, 0.12)',
      glow: 'rgba(212, 184, 150, 0.28)',
      // LAUNCH-1 — was '/finances', which is not a registered route;
      // the CTA 404'd from the first screen after every login.
      href: '/settings?tab=payments',
      icon: DollarSign,
    },
  ];

  const [openId, setOpenId] = useState<RingSpec['id'] | null>(null);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border dark:border-white/[0.10] bg-card p-6 shadow-sm orbit-box-dark">
      {/* Eyebrow + headline */}
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            This week
          </p>
          <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-foreground">
            Your business's shape.
          </h2>
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {labelForRange(weekRange)}
        </span>
      </div>

      <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-[auto_1fr]">
        {/* Concentric rings */}
        <RingStack rings={rings} />

        {/* Per-ring metric rows */}
        <div className="flex flex-col gap-2">
          {rings.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setOpenId(r.id)}
              className="group flex items-center justify-between rounded-xl border border-transparent px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full"
                  style={{ background: r.glow, color: r.color }}
                >
                  <r.icon className="h-3.5 w-3.5" />
                </span>
                <div className="flex flex-col">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {r.label}
                  </span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                    {r.format(r.value)}{' '}
                    <span className="text-muted-foreground/60">
                      / {r.format(r.goal)}
                    </span>
                  </span>
                </div>
              </div>
              <OrbitIcon name="arrow.right" fallback={ArrowRight} className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          ))}
        </div>
      </div>
      {rings.map((r) => (
        <MetricDetailSheet
          key={r.id}
          open={openId === r.id}
          onOpenChange={(o) => setOpenId(o ? r.id : null)}
          icon={r.icon}
          eyebrow="This week"
          label={r.label}
          value={r.format(r.value)}
          accent={r.color}
          context={`Target this week: ${r.format(r.goal)}. Actual so far: ${r.format(r.value)} (${Math.round((r.value / Math.max(1, r.goal)) * 100)}% of goal).`}
          cta={{ label: `Open ${r.label.toLowerCase()}`, href: r.href }}
        />
      ))}
    </div>
  );
}

// ── Ring renderer ───────────────────────────────────────────────────

function RingStack({ rings }: { rings: RingSpec[] }) {
  // Sizes: outer 144px, each inner ring 22px tighter than the outer
  // so the rings nest without overlap.
  const size = 156;
  const stroke = 14;
  const gap = 6;

  // Mount fade-in. Each ring animates from 0 to its real fraction
  // over 1000ms, eased outCubic. We drive this with a single
  // useState progress + requestAnimationFrame loop so all three rings
  // animate in lockstep — Apple's rings move together.
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const dur = 1100;
    const step = (now: number) => {
      const e = Math.min(1, (now - start) / dur);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - e, 3);
      setT(eased);
      if (e < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-hidden="false"
      role="img"
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        className="-rotate-90"
      >
        {rings.map((r, i) => {
          // Each ring shrinks by 2 * (stroke + gap) per level so they
          // nest concentrically with breathing room between.
          const radius = size / 2 - stroke / 2 - i * (stroke + gap);
          const circumference = 2 * Math.PI * radius;
          // Cap fraction at 100% — overshooting goal looks weird and
          // hides the achievement. Studios who hit 150% just see a
          // closed ring.
          const fraction = Math.min(1, r.value / Math.max(1, r.goal));
          const fillOffset = circumference * (1 - fraction * t);

          return (
            <g key={r.id}>
              {/* Track */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={r.track}
                strokeWidth={stroke}
              />
              {/* Filled portion */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={r.color}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={fillOffset}
                style={{
                  filter: fraction > 0
                    ? `drop-shadow(0 0 6px ${r.glow})`
                    : undefined,
                }}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Date helpers ────────────────────────────────────────────────────

/** Monday 00:00 → Sunday 23:59:59 in local time. ISO-week convention
 *  (Sunday-start would skew the "shot this week" reading for studios
 *  who treat Sunday as the start of next week's prep). */
function weekWindow(): { start: Date; end: Date } {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, …
  // Convert so Monday = 0, Sunday = 6.
  const fromMonday = (day + 6) % 7;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - fromMonday);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  end.setMilliseconds(-1);
  return { start, end };
}

/** Human-readable range label for the eyebrow ("Mar 10 – 16"). */
function labelForRange({ start, end }: { start: Date; end: Date }): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}
